import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useStats } from '../../lib/api/stats';
import { useMediaQuery } from '../../lib/useMediaQuery';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(n: number | undefined): string {
  if (n === undefined) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return n.toLocaleString();
  return String(n);
}

function tsRel(iso: string): string {
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  const m = Math.round(ms / 60_000);
  if (m < 60) return `${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h`;
  return `${Math.round(h / 24)} d`;
}

function initials(name: string): string {
  return String(name ?? '?').slice(0, 2).toUpperCase();
}

// ── StatCard ──────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string;
  onClick?: () => void;
}

function StatCard({ label, value, onClick }: StatCardProps) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: 18,
        borderRadius: 12,
        background: 'var(--lca-surface)',
        boxShadow: '0 0 0 1px var(--lca-hairline)',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{
        fontSize: 28,
        fontWeight: 700,
        fontFamily: 'var(--lca-font-display)',
        letterSpacing: '-0.02em',
        color: 'var(--lca-ink)',
        fontFeatureSettings: '"tnum"',
      }}>
        {value}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--lca-gray-500)', marginTop: 4 }}>{label}</div>
    </div>
  );
}

// ── SkeletonRows ──────────────────────────────────────────────────────────────

function SkeletonRows() {
  const widths = ['80%', '65%', '72%', '58%', '76%'];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {widths.map((w, i) => (
        <div key={i} style={{ height: 36, borderRadius: 6, background: 'var(--lca-gray-100)', width: w }} />
      ))}
    </div>
  );
}

// ── Widget ────────────────────────────────────────────────────────────────────

interface WidgetProps {
  title: string;
  onViewAll?: () => void;
  isLoading: boolean;
  isEmpty: boolean;
  children: React.ReactNode;
}

function Widget({ title, onViewAll, isLoading, isEmpty, children }: WidgetProps) {
  return (
    <div style={{ padding: 20, borderRadius: 12, background: 'var(--lca-surface)', boxShadow: '0 0 0 1px var(--lca-hairline)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontFamily: 'var(--lca-font-display)', fontSize: 15, fontWeight: 600, letterSpacing: '-0.012em', color: 'var(--lca-ink)' }}>
          {title}
        </div>
        {onViewAll && (
          <span onClick={onViewAll} style={{ fontSize: 12, color: 'var(--lca-blue)', cursor: 'pointer' }}>
            View all →
          </span>
        )}
      </div>
      {isLoading ? (
        <SkeletonRows />
      ) : isEmpty ? (
        <div style={{ fontSize: 12.5, color: 'var(--lca-gray-500)', padding: '8px 0' }}>No data yet</div>
      ) : (
        children
      )}
    </div>
  );
}

// ── CountChip ─────────────────────────────────────────────────────────────────

function CountChip({ count, label }: { count: number; label: string }) {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '1px 8px',
      borderRadius: 5,
      background: 'var(--lca-gray-100)',
      color: 'var(--lca-gray-700)',
      fontSize: 11,
      fontWeight: 600,
      fontFeatureSettings: '"tnum"',
      flexShrink: 0,
    }}>
      {count} {label}
    </span>
  );
}

// ── UserRow ───────────────────────────────────────────────────────────────────

interface UserRowProps {
  rank: number;
  username: string;
  email?: string;
  chip: React.ReactNode;
  onClick: () => void;
  isLast?: boolean;
}

function UserRow({ rank, username, email, chip, onClick, isLast }: UserRowProps) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 0',
        borderBottom: isLast ? 'none' : '1px solid var(--lca-divider)',
        cursor: 'pointer',
      }}
    >
      <span style={{ fontSize: 11, color: 'var(--lca-gray-500)', width: 16, textAlign: 'right', flexShrink: 0, fontFeatureSettings: '"tnum"' }}>
        {rank}
      </span>
      <div style={{
        width: 24, height: 24, borderRadius: 999,
        background: 'var(--lca-gray-200)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 9, fontWeight: 700, color: 'var(--lca-gray-700)',
        flexShrink: 0,
      }}>
        {initials(username)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, color: 'var(--lca-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {username}
        </div>
        {email && (
          <div style={{ fontSize: 11, color: 'var(--lca-gray-500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {email}
          </div>
        )}
      </div>
      {chip}
    </div>
  );
}

// ── ConvoRow ──────────────────────────────────────────────────────────────────

interface ConvoRowProps {
  title: string;
  username: string;
  updatedAt: string;
  onClick: () => void;
  isLast?: boolean;
}

function ConvoRow({ title, username, updatedAt, onClick, isLast }: ConvoRowProps) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 0',
        borderBottom: isLast ? 'none' : '1px solid var(--lca-divider)',
        cursor: 'pointer',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, color: 'var(--lca-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {title || '(untitled)'}
        </div>
        <div style={{ fontSize: 11, color: 'var(--lca-gray-500)' }}>{username}</div>
      </div>
      <div style={{ fontSize: 11, color: 'var(--lca-gray-500)', flexShrink: 0, fontFeatureSettings: '"tnum"' }}>
        {tsRel(updatedAt)}
      </div>
    </div>
  );
}

// ── Root screen ───────────────────────────────────────────────────────────────

export function OverviewScreen() {
  const navigate = useNavigate();
  const { data: stats, isLoading } = useStats();

  const isTablet = useMediaQuery('(max-width: 1100px)');
  const isMobile = useMediaQuery('(max-width: 768px)');

  const statsGridCols   = isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)';
  const widgetsGridCols = isMobile ? '1fr' : '1fr 1fr';

  const topUsers        = stats?.topUsersByConversations ?? [];
  const latestConvos    = stats?.latestConversations ?? [];
  const mostActiveUsers = stats?.mostActiveUsers7d ?? [];

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <h1 style={{
        fontFamily: 'var(--lca-font-display)',
        fontSize: 22,
        fontWeight: 700,
        letterSpacing: '-0.02em',
        color: 'var(--lca-ink)',
        margin: 0,
      }}>
        Overview
      </h1>

      {/* ── Stat cards ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: statsGridCols, gap: 12 }}>
        <StatCard
          label="Total Users"
          value={fmt(stats?.totalUsers)}
          onClick={() => void navigate({ to: '/users' })}
        />
        <StatCard
          label="Total Conversations"
          value={fmt(stats?.totalConversations)}
          onClick={() => void navigate({ to: '/conversations' })}
        />
        <StatCard
          label="Total Messages"
          value={fmt(stats?.totalMessages)}
        />
        <StatCard
          label="Active Users · 7d"
          value={fmt(stats?.activeUsers7d)}
        />
      </div>

      {/* ── Widgets grid ───────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: widgetsGridCols, gap: 12 }}>

        {/* Widget 1: Top Users by Conversations */}
        <Widget
          title="Top Users by Conversations"
          onViewAll={() => void navigate({ to: '/users' })}
          isLoading={isLoading}
          isEmpty={topUsers.length === 0}
        >
          {topUsers.map((u, i) => (
            <UserRow
              key={u.userId}
              rank={i + 1}
              username={u.username}
              email={u.email}
              chip={<CountChip count={u.count} label="convos" />}
              onClick={() => void navigate({ to: '/users' })}
              isLast={i === topUsers.length - 1}
            />
          ))}
        </Widget>

        {/* Widget 2: Latest Conversations */}
        <Widget
          title="Latest Conversations"
          onViewAll={() => void navigate({ to: '/conversations' })}
          isLoading={isLoading}
          isEmpty={latestConvos.length === 0}
        >
          {latestConvos.map((c, i) => (
            <ConvoRow
              key={c._id}
              title={c.title}
              username={c.username}
              updatedAt={c.updatedAt}
              // cast: cross-route navigate with search param; TanStack Router narrows search type per-route
              onClick={() => void navigate({ to: '/conversations', search: { selected: c._id } as never })}
              isLast={i === latestConvos.length - 1}
            />
          ))}
        </Widget>

        {/* Widget 3: Most Active Users — spans full row on desktop/tablet */}
        <div style={{ gridColumn: isMobile ? undefined : '1 / -1' }}>
          <Widget
            title="Most Active Users · 7d"
            onViewAll={() => void navigate({ to: '/users' })}
            isLoading={isLoading}
            isEmpty={mostActiveUsers.length === 0}
          >
            {mostActiveUsers.map((u, i) => (
              <UserRow
                key={u.userId}
                rank={i + 1}
                username={u.username}
                email={u.email}
                chip={<CountChip count={u.messageCount} label="msgs" />}
                onClick={() => void navigate({ to: '/users' })}
                isLast={i === mostActiveUsers.length - 1}
              />
            ))}
          </Widget>
        </div>

      </div>
    </div>
  );
}
